import { MsalProvider } from '@azure/msal-react'
import { Navigation } from './navigation/Navigation'
import { b2cMsalConfig } from './authConfig'
import { EventType, PublicClientApplication } from '@azure/msal-browser'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from 'components/ThemeProvider'
import { AccessTokenProvider } from './contexts/AccessTokenContext'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { AppInsightsErrorBoundary } from '@microsoft/applicationinsights-react-js'
import {
  appInsights,
  clearAppInsightsUserContext,
  reactPlugin,
  setAppInsightsUserContext,
} from './utils/appInsights'
import { ErrorIndicator } from 'components/Shared/ErrorIndicator'
import { Container } from '@mui/material'
import './sentry'
import { useSentryIdentification } from 'hooks/useSentryIdentification'

const msalInstance = new PublicClientApplication(b2cMsalConfig)

const syncAppInsightsUserContext = (account?: {
  homeAccountId?: string
  localAccountId?: string
  name?: string
  username?: string
}) => {
  const authenticatedUserId = account?.localAccountId ?? account?.homeAccountId

  if (!account || !authenticatedUserId) {
    clearAppInsightsUserContext()
    return
  }

  setAppInsightsUserContext({
    authenticatedUserId,
    displayName: account.name ?? account.username,
  })
}

msalInstance.addEventCallback((event: any) => {
  if (event.eventType === EventType.LOGOUT_SUCCESS) {
    clearAppInsightsUserContext()
  }

  if (
    [
      EventType.LOGIN_SUCCESS,
      EventType.ACQUIRE_TOKEN_SUCCESS,
      EventType.SSO_SILENT_SUCCESS,
    ].includes(event.eventType) &&
    event.payload.account
  ) {
    const account = event.payload.account

    msalInstance.setActiveAccount(account)
    syncAppInsightsUserContext(account)
  }
})

const queryClient = new QueryClient()
appInsights.loadAppInsights()

const initialAccount =
  msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0]

if (initialAccount) {
  msalInstance.setActiveAccount(initialAccount)
}

syncAppInsightsUserContext(initialAccount)

export const App = () => {
  useSentryIdentification()

  return (
    <ThemeProvider>
      <AppInsightsErrorBoundary
        onError={() => {
          return (
            <Container>
              <ErrorIndicator />
            </Container>
          )
        }}
        appInsights={reactPlugin}
      >
        <MsalProvider instance={msalInstance}>
          <QueryClientProvider client={queryClient}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <AccessTokenProvider>
                <Navigation />
              </AccessTokenProvider>
            </LocalizationProvider>
          </QueryClientProvider>
        </MsalProvider>
      </AppInsightsErrorBoundary>
    </ThemeProvider>
  )
}
